import React, { useCallback, useState } from 'react';
import { Box, Card, CardContent, CircularProgress, Grid, Button } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import Divider from 'components/Shared/Divider';
import FormText from 'components/Shared/FormFields/FormText';
import { useSnackbar } from 'notistack';
import { createAccessGroup } from 'services/user';
import { getClients, client } from 'services/clients';
import { useNavigate } from 'react-router-dom';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import AssignUsersModal from 'components/Pages/Admin/AccessGroups/AssignUsersModal';
import { CustomTypography } from 'components/Shared/TopPage/styles';
import { SubmitButton } from './styles';

type AccessGroupForm = {
  name: string;
  display_name: string;
  clients?: number[];
};

type InvalidProps = {
  name?: boolean;
  display_name?: boolean;
};

function NewAccessGroup() {
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();

  const [form, setForm] = useState<AccessGroupForm>({
    name: '',
    display_name: '',
    clients: []
  });

  const [invalid, setInvalid] = useState<InvalidProps>({});
  const [isSubmitting, setSubmitting] = useState(false);

  const [clients, setClients] = useState<client[]>([]);
  const [selectedClients, setSelectedClients] = useState<client[]>([]);
  const [isLoadingClients, setLoadingClients] = useState(false);

  const [createdAccessGroupId, setCreatedAccessGroupId] = useState<number | null>(null);
  const [assignModalOpen, setAssignModalOpen] = useState(false);

  const fetchClients = useCallback((search: string) => {
    setLoadingClients(true);

    getClients(
      data => {
        setClients(data);
        setLoadingClients(false);
      },
      {
        perPage: 1000000,
        page: 1,
        filters: search ? `&without_access_group=1&search=${encodeURIComponent(search)}` : '&without_access_group=1'
      }
    );
  }, []);

  const handleValidate = () => {
    const errors: InvalidProps = {};
    if (!form.name || !/^[a-z0-9]+$/.test(form.name)) {
      errors.name = true;
    }
    if (!form.display_name) {
      errors.display_name = true;
    }

    setInvalid(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = () => {
    if (!handleValidate()) {
      enqueueSnackbar('Verifique os campos obrigatórios e URLs válidas', { variant: 'error' });
      return;
    }

    setSubmitting(true);

    createAccessGroup(
      {
        ...form,
        clients: selectedClients.map(c => c.id)
      },
      created => {
        setSubmitting(false);
        enqueueSnackbar('Grupo de Acesso criado com sucesso!', { variant: 'success' });

        if (created && created.id) {
          setCreatedAccessGroupId(created.id);
          setAssignModalOpen(true);
        }

        setForm({
          name: '',
          display_name: '',
          clients: []
        });
        setSelectedClients([]);
      },
      (error: string) => {
        setSubmitting(false);
        enqueueSnackbar(error, { variant: 'error' });
      }
    );
  };

  const handleBackClick = () => {
    navigate('/grupos-acesso');
  };

  return (
    <>
      <Box display='flex' alignItems='center'>
        <Button startIcon={<ArrowBackIcon />} onClick={handleBackClick} variant='text' style={{ marginBottom: '16px' }}>
          Voltar
        </Button>
        <CustomTypography variant='h1' sx={{ mb: 0 }}>
          Novo Grupo de Acesso
        </CustomTypography>
      </Box>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Divider title='Informações do Grupo de Acesso' />
              <Grid container spacing={2} pt={2}>
                <Grid item md={6} xs={12}>
                  <FormText
                    label='Identificador (sem espaços ou caracteres especiais)'
                    value={form.name}
                    required
                    onChange={e => setForm({ ...form, name: e.target.value.toLowerCase().replace(/[^a-z0-9]/g, '') })}
                    error={invalid.name}
                    helperText={invalid.name ? 'Campo obrigatório, use apenas letras minúsculas e números' : ''}
                    sx={{
                      '& .MuiOutlinedInput-root': { borderRadius: selectedClients.length > 0 ? 4 : 10 }
                    }}
                  />
                </Grid>
                <Grid item md={6} xs={12}>
                  <FormText
                    label='Nome do Cliente'
                    value={form.display_name}
                    onChange={e => setForm({ ...form, display_name: e.target.value })}
                    error={invalid.display_name}
                    required
                    helperText={invalid.display_name ? 'Campo obrigatório' : ''}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 10 } }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Autocomplete
                    multiple
                    options={clients}
                    value={selectedClients}
                    loading={isLoadingClients}
                    filterSelectedOptions
                    isOptionEqualToValue={(option: client, value: client) => option.id === value.id}
                    onChange={(_event: React.SyntheticEvent, newValue: client[]) => setSelectedClients(newValue)}
                    onInputChange={(_event: React.SyntheticEvent, value: string) => fetchClients(value)}
                    onOpen={() => fetchClients('')}
                    getOptionLabel={(option: client) => option.name || `${option.cnpj}`}
                    size='small'
                    loadingText='Carregando...'
                    noOptionsText='Nenhum cliente encontrado.'
                    renderInput={params => (
                      <TextField
                        {...params}
                        required
                        label='* Adicionar Clientes por CNPJ'
                        placeholder='Digite para buscar...'
                        InputLabelProps={{ style: { fontSize: 13 } }}
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {isLoadingClients ? <CircularProgress color='inherit' size={20} /> : null}
                              {params.InputProps.endAdornment}
                            </>
                          )
                        }}
                        fullWidth
                      />
                    )}
                    sx={{
                      input: { fontSize: '0.9rem' },
                      '& .MuiOutlinedInput-root': { borderRadius: selectedClients.length > 0 ? 4 : 10 }
                    }}
                  />
                </Grid>
              </Grid>

              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: '50px' }}>
                <SubmitButton
                  color='secondary'
                  variant='contained'
                  onClick={handleSubmit}
                  disableElevation
                  disabled={isSubmitting || !form.name || !form.display_name || selectedClients.length === 0}
                >
                  {isSubmitting ? <CircularProgress size={16} /> : 'Cadastrar'}
                </SubmitButton>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      <AssignUsersModal
        open={assignModalOpen}
        accessGroupId={createdAccessGroupId}
        onClose={() => setAssignModalOpen(false)}
        onAssigned={() => {
          setAssignModalOpen(false);
        }}
      />
    </>
  );
}

export default NewAccessGroup;
